import { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { motion, AnimatePresence } from 'framer-motion'
import { FiGithub, FiExternalLink } from 'react-icons/fi'
import type { Language } from '../App'
import { t } from '../utils/translations'
import { projects } from '../utils/data'
import './Projects.css'

interface ProjectsProps { language: Language }

const categories = ['All', 'Full Stack', 'FinTech', 'E-Commerce', 'AI/ML', 'Mobile', 'Dev Tools']

export default function Projects({ language }: ProjectsProps) {
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true })
  const [activeFilter, setActiveFilter] = useState('All')

  const filtered = activeFilter === 'All' ? projects : projects.filter(p => p.category === activeFilter)

  return (
    <section id="projects" ref={ref}>
      <div className="orb orb-pink proj-orb" />
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ y: 30, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label">{t('proj.label', language)}</p>
          <h2 className="section-title">{t('proj.title', language)}</h2>
          <div className="gradient-divider" />
          <p className="section-desc">{t('proj.desc', language)}</p>
        </motion.div>

        {/* Filter tabs */}
        <motion.div
          className="proj-filters"
          initial={{ y: 20, opacity: 0 }}
          animate={inView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {categories.map(cat => (
            <button
              key={cat}
              className={`filter-btn ${activeFilter === cat ? 'active' : ''}`}
              onClick={() => setActiveFilter(cat)}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Featured projects */}
        <div className="proj-featured">
          <AnimatePresence mode="popLayout">
            {filtered.filter(p => p.featured).map((project, i) => (
              <motion.div
                key={project.id}
                className="proj-card proj-card--featured card"
                layout
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <div className="proj-visual" style={{ background: project.gradient }}>
                  <div className="proj-visual-text">{project.title.slice(0, 2)}</div>
                </div>

                <div className="proj-content">
                  <div className="proj-top">
                    <span className="proj-category tag">{project.category}</span>
                    <div className="proj-links">
                      <a href={project.github} target="_blank" rel="noopener noreferrer" className="proj-link" title="View code">
                        <FiGithub />
                      </a>
                      {/* <a href={project.demo} target="_blank" rel="noopener noreferrer" className="proj-link" title="Live demo">
                        <FiExternalLink />
                      </a> */}
                    </div>
                  </div>

                  <h3 className="proj-title">{project.title}</h3>
                  <p className="proj-desc">{project.description}</p>

                  <div className="proj-tech">
                    {project.tech.map(tech => (
                      <span key={tech} className="tag">{tech}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Other projects */}
        <div className="proj-grid">
          <AnimatePresence mode="popLayout">
            {filtered.filter(p => !p.featured).map((project, i) => (
              <motion.div
                key={project.id}
                className="proj-card card"
                layout
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 30, opacity: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="proj-mini-header">
                  <div className="proj-mini-icon" style={{ background: project.gradient }}>
                    {project.title.slice(0, 2)}
                  </div>
                  <div className="proj-links">
                    <a href={project.github} target="_blank" rel="noopener noreferrer" className="proj-link"><FiGithub /></a>
                    {/* <a href={project.demo} target="_blank" rel="noopener noreferrer" className="proj-link"><FiExternalLink /></a> */}
                  </div>
                </div>
                <span className="proj-category tag" style={{ marginBottom: '0.5rem', display: 'inline-flex' }}>{project.category}</span>
                <h3 className="proj-mini-title">{project.title}</h3>
                <p className="proj-mini-desc">{project.description}</p>
                <div className="proj-tech">
                  {project.tech.slice(0, 3).map(tech => <span key={tech} className="tag">{tech}</span>)}
                  {project.tech.length > 3 && <span className="tag">+{project.tech.length - 3}</span>}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
